import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const TECH_STACK = [
  "React",
  "Node.js",
  "MySQL",
  "GA4 / GTM",
  "Looker Studio",
  "Clarity",
  "BigQuery",
  "Automation",
  "Accessibility",
];

function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setPrefersReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return prefersReduced;
}

function TypingIntro() {
  const fullText =
    "Hi, I'm Chanho Kim \u2014 a Full Stack Developer.\n\nI build production-ready web applications and analytics systems that improve user experience and performance.";

  const prefersReduced = usePrefersReducedMotion();

  const [displayedText, setDisplayedText] = useState(
    prefersReduced ? fullText : "",
  );
  const [index, setIndex] = useState(prefersReduced ? fullText.length : 0);
  const [doneTyping, setDoneTyping] = useState(prefersReduced);

  const skippedRef = useRef(false);

  const letters = useMemo(() => fullText.split(""), [fullText]);

  const skipTyping = useCallback(() => {
    if (doneTyping || skippedRef.current) return;
    skippedRef.current = true;
    setDisplayedText(fullText);
    setIndex(fullText.length);
    setDoneTyping(true);
  }, [doneTyping, fullText]);

  useEffect(() => {
    if (prefersReduced && !doneTyping) {
      setDisplayedText(fullText);
      setIndex(fullText.length);
      setDoneTyping(true);
    }
  }, [prefersReduced, doneTyping, fullText]);

  useEffect(() => {
    if (index >= letters.length) {
      if (!doneTyping) setDoneTyping(true);
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayedText((prev) => prev + letters[index]);
      setIndex((prev) => prev + 1);
    }, 50);

    return () => clearTimeout(timeout);
  }, [index, letters, doneTyping]);

  return (
    <div
      className="textbox"
      aria-live="polite"
      onClick={skipTyping}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          skipTyping();
        }
      }}
      role={doneTyping ? undefined : "button"}
      tabIndex={doneTyping ? undefined : 0}
      aria-label={
        doneTyping ? undefined : "Click or press Enter to skip typing animation"
      }
    >
      <p>
        {displayedText}
        <span className="caret" aria-hidden="true" />
      </p>

      {!doneTyping && (
        <span className="textbox__skip-hint" aria-hidden="true">
          Click to skip
        </span>
      )}

      <ul
        className={`tech-stack ${doneTyping ? "tech-stack--visible" : ""}`}
        aria-label="Core technologies"
      >
        {TECH_STACK.map((tech) => (
          <li key={tech} className="tech-stack__item">
            {tech}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default TypingIntro;
